// pages/home.jsx
import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { motion } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import GetStartedFlow from '../components/getStarted/GetStartedFlow'; // eager import
import { getLocalPlatformFee } from '../utils/pricingCopy';
import {
  homeOverviewGroups,
  homePlatformPoints,
} from '../components/marketing/content';

export default function Home() {
  const { user, authLoading } = useAuth();
  const [flowOpen, setFlowOpen] = React.useState(false);
  const { feeLabel: localFeeLabel } = React.useMemo(() => getLocalPlatformFee('GBP'), []);

  if (authLoading) return null;
  if (user && !flowOpen) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <main className="marketing-root">
      <div className="container py-12 space-y-16">
        {/* HERO */}
        <section className="marketing-hero">
          <div className="marketing-orb orb-one" />
          <div className="marketing-orb orb-two" />
          <div className="marketing-hero__layout">
            <div>
              <p className="marketing-kicker">Member Nest for clubs</p>
              <h1 className="marketing-heading text-5xl font-bold mb-4">
                Run your club and make joining simpler.
              </h1>
              <p className="marketing-lede mb-6">
                Bring memberships, payments, events, and communication into one system, with a clear join journey for new members.
              </p>
              <div className="marketing-cta-row">
                <button onClick={() => setFlowOpen(true)} className="button button-primary px-6 py-3 text-lg">
                  Start in minutes
                </button>
                <Link to="/features" className="button button-secondary px-6 py-3 text-lg">
                  See how it works
                </Link>
              </div>
              <div className="marketing-proof">No credit card required • Invite members when you're ready • Cancel anytime</div>
            </div>

            <div className="marketing-hero__panel">
              <span className="marketing-pill">Simple setup</span>
              <h3 className="marketing-heading text-2xl font-semibold mt-3">
                Get started in 3 steps
              </h3>
              <div className="marketing-steps">
                {[
                  { title: 'Create your club', desc: 'Add your club details and choose the features you want to use.' },
                  { title: 'Set up membership', desc: 'Configure pricing, join forms, profile fields, and approvals.' },
                  { title: 'Share it with members', desc: 'Publish your join flow and start managing the club in one place.' },
                ].map((step, idx) => (
                  <div key={step.title} className="marketing-step">
                    <div className="marketing-step__index">{idx + 1}</div>
                    <div>
                      <p className="font-semibold">{step.title}</p>
                      <p className="text-gray-600 text-sm">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* OVERVIEW */}
        <section className="marketing-section">
          <div className="marketing-section__header">
            <h2 className="marketing-heading text-4xl font-semibold mb-3">What it includes</h2>
            <p className="marketing-lede">
              Everything most clubs need to manage members, take payments, run events, and give new joiners a smoother experience.
            </p>
          </div>
          <div className="marketing-grid">
            {homeOverviewGroups.map((group, idx) => (
              <motion.div
                key={group.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.06 }}
              >
                <Card className="marketing-card">
                  <div className="marketing-pill" style={{ marginBottom: '0.9rem' }}>{group.count}</div>
                  <h3 className="text-xl font-semibold mb-2">{group.title}</h3>
                  <p className="text-gray-600">{group.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="marketing-section">
          <div className="marketing-section__header">
            <h2 className="marketing-heading text-3xl font-semibold mb-2">Why it works</h2>
            <p className="marketing-lede">
              It works better for organisers and members when everything from joining to day-to-day admin happens in the same place.
            </p>
          </div>
          <div className="marketing-grid">
            {homePlatformPoints.map((point, i) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <div className="marketing-card">
                  <h4 className="text-xl font-semibold mb-2">{point.title}</h4>
                  <p className="text-gray-600">{point.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="marketing-section">
          <div className="marketing-split marketing-split--compact">
            <div>
              <p className="marketing-kicker">For clubs and members</p>
              <h2 className="marketing-heading text-3xl font-semibold mb-3">Better for the people running the club, and better for the people joining it</h2>
              <p className="marketing-lede" style={{ marginBottom: '1.5rem' }}>
                Clubs get one place to manage memberships, payments, events, and communication. Members get a clearer path to join, pay, stay up to date, and find what they need.
              </p>
              <div className="marketing-preview-surface">
                <div className="marketing-surface__title">For organisers</div>
                <div className="marketing-chip-list">
                  {['Memberships', 'Payments', 'Events', 'Communication', 'Documents'].map((item) => (
                    <span key={item} className="marketing-chip">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div className="marketing-preview-surface" style={{ marginTop: '1rem' }}>
                <div className="marketing-surface__title">For members</div>
                <div className="marketing-chip-list">
                  {['Simple joining', 'Online payment', 'Messages', 'Event updates', 'Member documents'].map((item) => (
                    <span key={item} className="marketing-chip marketing-chip--accent">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div className="marketing-cta-row">
                <Link to="/features" className="button button-primary px-6 py-3">
                  Explore features
                </Link>
                <Link to="/demo" className="button button-secondary px-6 py-3">
                  Book a walkthrough
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section className="marketing-section text-center">
          <h2 className="marketing-heading text-4xl font-semibold mb-3">Transparent pricing</h2>
          <p className="marketing-lede mb-6">{localFeeLabel} per member per year.</p>
          <Link to="/pricing" className="button button-primary px-6 py-3 text-lg">View full pricing</Link>
        </section>

        {/* Modal */}
        {flowOpen && <GetStartedFlow onClose={() => setFlowOpen(false)} />}
      </div>
    </main>
  );
}
